import type { BookedFlightItineraryWithDepartureTime } from "@b2bportal/air-booking-api";
import {
  FlightPostBookingHooks,
  FlightScheduleChangeBanner,
  FlightScheduleChangeModal,
} from "@b2bportal/core-flights";
import { useModuleBEM } from "@b2bportal/core-themes";
import type { ScheduleChangeDiffModel } from "@b2bportal/core-types";
import { useI18nContext } from "@hopper-b2b/i18n";
import clsx from "clsx";
import { FlightSegmentCard } from "../FlightDetailsPanel/FlightSegmentCard";
import styles from "./FlightScheduleChangeComparisonPanel.module.scss";
import { useFlightScheduleChangeComparisonPanel } from "./useFlightScheduleChangeComparisonPanel";

export interface FlightScheduleChangeComparisonPanelProps {
  className?: string;
  itinerary: BookedFlightItineraryWithDepartureTime;
  diffModel: ScheduleChangeDiffModel;
}

export const FlightScheduleChangeComparisonPanel = (
  props: FlightScheduleChangeComparisonPanelProps
) => {
  const { className, itinerary, diffModel } = props;
  const [block, cn] = useModuleBEM(
    styles,
    "FlightScheduleChangeComparisonPanel"
  );
  const { t } = useI18nContext();
  const scheduleChangeData = FlightPostBookingHooks.useScheduleChange(
    props.itinerary
  );
  const { sliceComparatorList, airports, airlines } =
    useFlightScheduleChangeComparisonPanel({
      itinerary,
      diffModel,
    });

  return (
    <div className={clsx(block, className)}>
      <FlightScheduleChangeBanner itinerary={itinerary} hideActionButton />
      <div className={cn("schedule-change-details")}>
        <div className={cn("section")}>
          <div className={cn("section-header")}>
            <h3 className={cn("section-title")}>
              {t("core-flights.postbooking.scheduleChange.updatedItinerary")}
            </h3>
            <span className={cn("updated-badge")}>
              {t("core-flights.postbooking.scheduleChange.updatedBadge")}
            </span>
          </div>

          <div className={cn("flights-container")}>
            {sliceComparatorList.map((sliceComparator) => {
              const { updatedSliceSummary, sliceKey } = sliceComparator;

              return (
                <FlightSegmentCard
                  key={`updated-${sliceKey}`}
                  className={cn("flight-slice")}
                  segment={updatedSliceSummary.firstSegment}
                  lastSegment={updatedSliceSummary.lastSegment}
                  totalDurationMinutes={
                    updatedSliceSummary.totalDurationMinutes
                  }
                  stopsCount={updatedSliceSummary.stopsCount}
                  displayDestinationCode={
                    updatedSliceSummary.displayDestinationCode
                  }
                  airportMap={airports}
                  airlineMap={airlines}
                />
              );
            })}
          </div>
        </div>

        <div className={cn("section")}>
          <h3 className={cn("section-title")}>
            {t("core-flights.postbooking.scheduleChange.originalItinerary")}
          </h3>

          <div className={cn("flights-container")}>
            {sliceComparatorList.map((sliceComparator) => {
              const { originalSliceSummary, sliceKey } = sliceComparator;

              return (
                <FlightSegmentCard
                  key={`original-${sliceKey}`}
                  className={cn("flight-slice")}
                  segment={originalSliceSummary.firstSegment}
                  lastSegment={originalSliceSummary.lastSegment}
                  totalDurationMinutes={
                    originalSliceSummary.totalDurationMinutes
                  }
                  stopsCount={originalSliceSummary.stopsCount}
                  displayDestinationCode={
                    originalSliceSummary.displayDestinationCode
                  }
                  airportMap={airports}
                  airlineMap={airlines}
                />
              );
            })}
          </div>
        </div>
      </div>

      <FlightScheduleChangeModal
        hoursToDeparture={itinerary.hoursToDeparture}
        itinerary={itinerary.bookedItinerary}
        scheduleChangeDiffs={diffModel}
        triggerCtaLabel={scheduleChangeData.copy.scheduleChangeCtaLabel}
      />
    </div>
  );
};
